import { Check, ChevronDown, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { fuzzyFilter } from '../lib/fuzzy';
import { Avatar, AvatarStack } from './Avatar';
import { SearchInput } from './Input';
import { Popover, usePopover } from './Popover';
import selectStyles from './Select.module.css';
import styles from './PersonPicker.module.css';

// R3 (JB-2, handoff R3 build order 3): one person picked from the people in a venture, each
// with their avatar. A button that opens an anchored list, like Select — never a raw control
// (CLAUDE.md §5). `PeoplePicker` below is the many-people version, for the interview planner (R7).

export interface PersonOption {
  readonly id: string;
  readonly fullName: string;
  readonly email?: string;
}

interface PersonPickerProps {
  readonly label: string;
  readonly people: readonly PersonOption[];
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly placeholder?: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  /** Shows a clear button; leave it off where the field is required. */
  readonly isClearable?: boolean;
  readonly id?: string;
  readonly describedBy?: string;
}

/** A short list is scanned by eye; past this it gets a filter box. */
const FILTER_THRESHOLD = 6;

export function PersonPicker({
  label,
  people,
  value,
  onChange,
  placeholder = 'Choose someone',
  isInvalid = false,
  disabled = false,
  isClearable = false,
  id,
  describedBy,
}: PersonPickerProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const selected = people.find((person) => person.id === value) ?? null;
  const filtered = useMemo(
    () => fuzzyFilter(query, people, (person) => `${person.fullName} ${person.email ?? ''}`).map((result) => result.item),
    [query, people],
  );

  function pick(next: string | null): void {
    onChange(next);
    setQuery('');
    close();
  }

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`${selectStyles.trigger} ${isInvalid ? selectStyles.invalid : ''}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-describedby={describedBy}
        aria-invalid={isInvalid || undefined}
        aria-label={selected ? `${label}: ${selected.fullName}` : label}
      >
        {selected && <Avatar name={selected.fullName} size="sm" />}
        <span className={`${selectStyles.value} ${selected ? '' : selectStyles.placeholder}`}>{selected?.fullName ?? placeholder}</span>
        <span className={selectStyles.chevron} aria-hidden>
          <ChevronDown size={16} />
        </span>
      </button>
      {isClearable && selected && !disabled && (
        <button type="button" className={styles.clear} aria-label={`Clear ${label}`} onClick={() => onChange(null)}>
          <X size={14} aria-hidden />
        </button>
      )}

      <Popover anchor={anchor} onClose={close} label={label}>
        {people.length > FILTER_THRESHOLD && (
          <div className={selectStyles.filterRow}>
            <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a person" autoFocus />
          </div>
        )}
        <div role="listbox" aria-label={label}>
          {filtered.length === 0 && <p className={selectStyles.noOptions}>Nobody matches</p>}
          {filtered.map((person) => (
            <button
              key={person.id}
              type="button"
              role="option"
              aria-selected={person.id === value}
              className={selectStyles.option}
              onClick={() => pick(person.id)}
            >
              <Avatar name={person.fullName} size="sm" />
              <span className={selectStyles.optionText}>
                {person.fullName}
                {person.email !== undefined && <span className={selectStyles.optionDescription}>{person.email}</span>}
              </span>
              {person.id === value && (
                <span className={selectStyles.optionTick} aria-hidden>
                  <Check size={15} />
                </span>
              )}
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}

interface PeoplePickerProps {
  readonly label: string;
  readonly people: readonly PersonOption[];
  readonly value: readonly string[];
  /** Once, when the list closes, and only if the choice changed — one save per edit. */
  readonly onChange: (value: readonly string[]) => void;
  readonly placeholder?: string;
  /** Unticking the last one is refused: an interview keeps an interviewer. */
  readonly isRequired?: boolean;
  readonly disabled?: boolean;
  /**
   * Opt-in: what the closed picker shows while someone is chosen, in place of the avatars and
   * names (PLN-01's pills, Redesign Group 4). The trigger grows to fit it.
   */
  readonly valueContent?: ReactNode;
}

/** R7 PLN-01's Interviewer(s) cell: several people, with their avatars, ticked in one list. */
export function PeoplePicker({ label, people, value, onChange, placeholder = 'Choose people', isRequired = false, disabled = false, valueContent }: PeoplePickerProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<readonly string[]>(value);
  const chosen = people.filter((person) => value.includes(person.id));
  const filtered = useMemo(
    () => fuzzyFilter(query, people, (person) => `${person.fullName} ${person.email ?? ''}`).map((result) => result.item),
    [query, people],
  );

  function open(): void {
    setDraft(value);
    setQuery('');
    toggle();
  }

  function finish(): void {
    close();
    const isSame = draft.length === value.length && draft.every((id) => value.includes(id));
    if (!isSame) onChange(draft);
  }

  function flip(id: string): void {
    setDraft((current) => {
      if (!current.includes(id)) return [...current, id];
      if (isRequired && current.length === 1) return current;
      return current.filter((other) => other !== id);
    });
  }

  const names = chosen.map((person) => person.fullName);
  const isCustom = valueContent !== undefined && names.length > 0;
  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={isCustom ? `${selectStyles.trigger} ${styles.grows}` : selectStyles.trigger}
        onClick={isOpen ? finish : open}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={names.length > 0 ? `${label}: ${names.join(', ')}` : label}
      >
        {isCustom ? (
          <span className={styles.custom}>{valueContent}</span>
        ) : (
          <>
            {names.length > 0 && <AvatarStack names={names} />}
            <span className={`${selectStyles.value} ${names.length > 0 ? '' : selectStyles.placeholder}`}>
              {names.length === 0 ? placeholder : names.length === 1 ? names[0] : `${names[0] ?? ''} +${names.length - 1}`}
            </span>
          </>
        )}
        <span className={selectStyles.chevron} aria-hidden>
          <ChevronDown size={16} />
        </span>
      </button>
      <Popover anchor={anchor} onClose={finish} label={label}>
        {people.length > FILTER_THRESHOLD && (
          <div className={selectStyles.filterRow}>
            <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a person" autoFocus />
          </div>
        )}
        <div role="listbox" aria-label={label} aria-multiselectable>
          {filtered.length === 0 && <p className={selectStyles.noOptions}>Nobody matches</p>}
          {filtered.map((person) => {
            const isChosen = draft.includes(person.id);
            return (
              <button key={person.id} type="button" role="option" aria-selected={isChosen} className={selectStyles.option} onClick={() => flip(person.id)}>
                <Avatar name={person.fullName} size="sm" />
                <span className={selectStyles.optionText}>
                  {person.fullName}
                  {person.email !== undefined && <span className={selectStyles.optionDescription}>{person.email}</span>}
                </span>
                {isChosen && (
                  <span className={selectStyles.optionTick} aria-hidden>
                    <Check size={15} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Popover>
    </div>
  );
}
